<template>
  <div
    class="flex flex-shrink-0 flex-nowrap justify-start gap-0.5"
    :class="{
      'pointer-events-none opacity-25': localEntries.isLimitReached.value,
    }"
  >
    <ToolboxButton
      v-for="button in buttons"
      :key="button.mode"
      v-tooltip.bottom="button.tooltip"
      :active="modeModel === button.mode"
      :disabled="disabled"
      class="rounded"
      :title="button.title"
      :aria-label="button.title"
      @click="modeModel = button.mode"
    >
      <template #default="{ active }">
        <component
          :is="button.icon"
          :class="button.sizes"
          v-bind="{ active }"
        />
      </template>
    </ToolboxButton>
    <div
      class="flex rounded md:hover:bg-gray-200"
      :class="{ 'md:hover:bg-lexmea-selection-lighter': modeModel === 'mark' }"
    >
      <ToolboxButton
        v-tooltip.bottom="t('legal_text.textEditor.tooltips.marker')"
        :active="modeModel === 'mark'"
        :disabled="disabled"
        class="rounded-l pl-[0.2rem] pr-0"
        :aria-label="t('legal_text.textEditor.tooltips.marker')"
        @click="modeModel = 'mark'"
      >
        <template #default="{ active }">
          <MarkerIcon
            class="h-5 w-5"
            :focus="active"
            v-bind="{ active }"
            :colorClass="
              'text-entry-' + markerColor.color + '-' + markerColor.color_type
            "
          />
        </template>
      </ToolboxButton>
      <MarkerMenu
        :class="{ 'bg-lexmea-selection-lightest': modeModel === 'mark' }"
        :active="modeModel === 'mark'"
        :modelValue="markerColor"
        @update:modelValue="setMarkerColor"
      />
    </div>
    <ToolboxButton
      v-tooltip.bottom="t('legal_text.textEditor.tooltips.eraser')"
      :active="markingMode === 'erasing'"
      :disabled="disabled"
      class="rounded !px-1"
      :title="t('legal_text.textEditor.tooltips.eraser')"
      :aria-label="t('legal_text.textEditor.tooltips.eraser')"
      @click="modeModel = 'erasing'"
    >
      <template #default="{ active }">
        <i-ph-eraser-fill :focus="active" class="h-7 w-7" />
      </template>
    </ToolboxButton>
    <div
      class="flex rounded md:hover:bg-gray-200"
      :class="{
        'pointer-events-none opacity-25': disabled,
      }"
    >
      <ToolboxButton
        v-tooltip.bottom="t('legal_text.textEditor.tooltips.bookmark')"
        :active="false"
        :disabled="disabled"
        :activeClass="'stroke-current'"
        class="rounded-l pr-0"
        :title="t('legal_text.textEditor.buttons.bookmark')"
        :aria-label="t('legal_text.textEditor.tooltips.bookmark')"
        @click="handleBookmarkClick"
      >
        <template #default="{ active }">
          <BookmarkIcon
            class="h-5 w-5"
            :focus="active"
            v-bind="{ active }"
            :outlined="bookmark === undefined"
            :colorClass="`text-entry-${bookmark?.color}-${bookmark?.color_type}`"
          />
        </template>
      </ToolboxButton>
      <BookmarkMenu
        :modelValue="
          {
            color: bookmark?.color,
            color_type: bookmark?.color_type,
          } as ColorPicker
        "
        @update:modelValue="updateModel"
        @deleteBookmarks="deleteBookmarks"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { IBookmark } from "~/models/ILegalTextParagraph";
import type { IParagraphEntry } from "~/models/IUserEntry";
import type { ColorPicker } from "~/types/ColorPicker";

const { t } = useI18n();

const props = defineProps<{
  markingMode?: IParagraphEntry["type"] | "erasing";
  markerColor: ColorPicker;
  disabled?: boolean;
  bookmark?: IBookmark;
}>();

const emit = defineEmits<{
  "update:markingMode": [mode: IParagraphEntry["type"] | undefined | "erasing"];
  "update:modelValue": [color: ColorPicker];
  deleteBookmark: [];
  addBookmark: [color: ColorPicker];
  "update:bookmark": [color: ColorPicker];
  "update:bookmarkText": [string | null];
}>();

const { markingMode: modeModel, markerColor } = useVModels(props, emit);

const buttons = useToolboxButtons();
const setMarkerColor = (markerColor: ColorPicker) => {
  emit("update:modelValue", markerColor);

  if (modeModel?.value !== "mark") {
    emit("update:markingMode", "mark");
  }
};

const localEntries = useLocalEntriesSync();

const showBookmarkHelperText = (text: string) => {
  emit("update:bookmarkText", text);

  setTimeout(function () {
    emit("update:bookmarkText", null);
  }, 3000);
};

const updateModel = (selectedColor: ColorPicker) => {
  if (!props.bookmark) {
    emit("addBookmark", selectedColor);
    showBookmarkHelperText(
      t("legal_text.textEditor.helper_texts.bookmarkAdded")
    );
  } else if (JSON.stringify(bookmarkColor) !== JSON.stringify(selectedColor)) {
    emit("update:bookmark", selectedColor);
  }
};

const deleteBookmarks = (): void => {
  if (props.bookmark) {
    emit("deleteBookmark");
    showBookmarkHelperText(
      t("legal_text.textEditor.helper_texts.bookmarkRemoved")
    );
  }
};

const handleBookmarkClick = () => {
  const id = props.bookmark?.id;
  if (id) {
    emit("deleteBookmark");
    showBookmarkHelperText(
      t("legal_text.textEditor.helper_texts.bookmarkRemoved")
    );
  } else {
    emit("addBookmark", bookmarkColor());
    showBookmarkHelperText(
      t("legal_text.textEditor.helper_texts.bookmarkAdded")
    );
  }

  emit("update:markingMode", undefined);
};

const bookmarkColor = (): ColorPicker => {
  if (props.bookmark?.color) {
    return {
      color: props.bookmark?.color,
      color_type: props.bookmark?.color_type,
    };
  }

  return { color: "yellow", color_type: "darkest" };
};
</script>
